// src/utils/downloadUtils.ts
import html2canvas from 'html2canvas';

export interface DownloadOptions {
  fileName?: string;
  backgroundColor?: string;
  scale?: number;
  watermark?: {
    logoPath: string;
    text: string;
  };
}

/**
 * Capture un élément HTML (invocation) en PNG sans effet d'opacité.
 */
export const downloadCardAsImage = async (
  element: HTMLElement,
  opts: DownloadOptions = {},
): Promise<void> => {
  const fileName = opts.fileName ?? 'invocation';
  const backgroundColor = opts.backgroundColor ?? '#ffffff';

  /*──────────────────────────── Clone hors‑viewport ────────────────────*/
  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed';
  wrapper.style.top = '0';
  wrapper.style.left = '-200000px';
  wrapper.style.pointerEvents = 'none';
  wrapper.style.zIndex = '-1000';

  // Attendre que le DOM soit complètement rendu
  await new Promise(r => requestAnimationFrame(r));
  await new Promise(r => setTimeout(r, 200));

  // Créer un clone avec cloneNode pour préserver la structure
  const clone = element.cloneNode(true) as HTMLElement;
  
  // Spécifiquement cibler les éléments hadith-context qui posent problème
  const originalHadithContexts = element.querySelectorAll('.hadith-context');
  const clonedHadithContexts = clone.querySelectorAll('.hadith-context');
  
  originalHadithContexts.forEach((original, index) => {
    if (clonedHadithContexts[index]) {
      // Forcer la copie du contenu et des styles
      clonedHadithContexts[index].innerHTML = original.innerHTML;
      clonedHadithContexts[index].textContent = original.textContent;
      
      // Copier tous les styles calculés
      const computedStyle = window.getComputedStyle(original);
      const clonedElement = clonedHadithContexts[index] as HTMLElement;
      
      // Forcer l'affichage
      clonedElement.style.display = computedStyle.display;
      clonedElement.style.visibility = 'visible';
      clonedElement.style.opacity = '1';
      clonedElement.style.color = computedStyle.color;
      clonedElement.style.backgroundColor = computedStyle.backgroundColor;
      clonedElement.style.fontSize = computedStyle.fontSize;
      clonedElement.style.fontFamily = computedStyle.fontFamily;
      clonedElement.style.lineHeight = computedStyle.lineHeight;
      clonedElement.style.padding = computedStyle.padding;
      clonedElement.style.margin = computedStyle.margin;
    }
  });

  clone.querySelector('.download-button')?.remove();
  clone.querySelector('.individual-download-container')?.remove();

  const { width, height } = element.getBoundingClientRect();
  clone.style.setProperty('width', `${width}px`, 'important');
  clone.style.setProperty('height', `${height}px`, 'important');
  clone.style.setProperty('background-color', backgroundColor, 'important');
  clone.style.setProperty('opacity', '1', 'important');
  clone.style.setProperty('filter', 'none', 'important');

  // Force 100 % d'opacité sur *tous* les descendants (override !important)
  clone.querySelectorAll('*').forEach(el => {
    (el as HTMLElement).style.setProperty('opacity', '1', 'important');
    (el as HTMLElement).style.setProperty('filter', 'none', 'important');
    (el as HTMLElement).style.setProperty('--tw-bg-opacity', '1', 'important');
    (el as HTMLElement).style.setProperty('--tw-text-opacity', '1', 'important');
  });

  if (opts.watermark) clone.appendChild(createWatermark(opts.watermark.logoPath, opts.watermark.text));

  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  await waitForImages(clone);
  await new Promise(r => requestAnimationFrame(r));

  const canvas = await html2canvas(clone, {
    background: backgroundColor,
    useCORS: true,
    allowTaint: true,
  });

  wrapper.remove();

  triggerDownload(canvas.toDataURL('image/png'), `${fileName}.png`);
};

/*──────────────────────────── Helpers ────────────────────────────*/
function triggerDownload(dataURL: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataURL;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function createWatermark(logoPath: string, text: string): HTMLElement {
  const wm = document.createElement('div');
  wm.style.cssText = `
    position:absolute;bottom:5px;left:20px;display:flex;align-items:center;gap:10px;
    background:rgba(255,255,255,.95);padding:8px 12px;border-radius:50px;
    box-shadow:0 2px 10px rgba(0,0,0,.08);font-family:'Poppins',sans-serif;
    font-size:11px;font-weight:600;color:#2c3e50;`;

  const img = document.createElement('img');
  img.src = logoPath;
  img.alt = 'Logo';
  img.style.cssText = 'width:40px;height:40px;border-radius:50%;object-fit:cover;';

  const span = document.createElement('span');
  span.textContent = text;

  wm.appendChild(img);
  wm.appendChild(span);
  return wm;
}

async function waitForImages(el: HTMLElement): Promise<void> {
  const imgs = el.querySelectorAll('img');
  await Promise.all(
    Array.from(imgs).map(img =>
      img.complete ? Promise.resolve() : new Promise((res, rej) => {
        img.onload = () => res(undefined);
        img.onerror = rej;
      }),
    ),
  );
}

export const downloadMultipleCards = async (
  elements: HTMLElement[],
  options: DownloadOptions = {},
): Promise<void> => {
  for (let i = 0; i < elements.length; i++) {
    await downloadCardAsImage(elements[i], {
      ...options,
      fileName: `${options.fileName || 'invocation'}-${i + 1}`,
    });
    await new Promise(r => setTimeout(r, 300));
  }
};

/**
 * Télécharge plusieurs cartes combinées en une seule image
 */
export const downloadCombinedCards = async (
  elements: HTMLElement[],
  options: DownloadOptions = {}
): Promise<void> => {
  const fileName = options.fileName ?? 'cartes-combinees';
  const backgroundColor = options.backgroundColor ?? '#ffffff';

  // Créer un conteneur pour combiner les éléments
  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed';
  wrapper.style.top = '0';
  wrapper.style.left = '-200000px';
  wrapper.style.pointerEvents = 'none';
  wrapper.style.zIndex = '-1000';
  wrapper.style.backgroundColor = backgroundColor;
  wrapper.style.padding = '20px';
  wrapper.style.display = 'flex';
  wrapper.style.flexDirection = 'column';
  wrapper.style.gap = '20px';
  wrapper.style.width = '800px';

  // Cloner et ajouter chaque élément
  elements.forEach(element => {
    // Créer un clone avec cloneNode pour préserver la structure
    const clone = element.cloneNode(true) as HTMLElement;
    
    // Spécifiquement cibler les éléments hadith-context qui posent problème
    const originalHadithContexts = element.querySelectorAll('.hadith-context');
    const clonedHadithContexts = clone.querySelectorAll('.hadith-context');
    
    originalHadithContexts.forEach((original, index) => {
      if (clonedHadithContexts[index]) {
        // Forcer la copie du contenu et des styles
        clonedHadithContexts[index].innerHTML = original.innerHTML;
        clonedHadithContexts[index].textContent = original.textContent;
        
        // Copier tous les styles calculés
        const computedStyle = window.getComputedStyle(original);
        const clonedElement = clonedHadithContexts[index] as HTMLElement;
        
        // Forcer l'affichage
        clonedElement.style.display = computedStyle.display;
        clonedElement.style.visibility = 'visible';
        clonedElement.style.opacity = '1';
        clonedElement.style.color = computedStyle.color;
        clonedElement.style.backgroundColor = computedStyle.backgroundColor;
        clonedElement.style.fontSize = computedStyle.fontSize;
        clonedElement.style.fontFamily = computedStyle.fontFamily;
        clonedElement.style.lineHeight = computedStyle.lineHeight;
        clonedElement.style.padding = computedStyle.padding;
        clonedElement.style.margin = computedStyle.margin;
      }
    });
    
    clone.querySelector('.download-button')?.remove();
    clone.querySelector('.individual-download-container')?.remove();
    
    // Styles pour chaque carte clonée
    clone.style.setProperty('width', '100%', 'important');
    clone.style.setProperty('margin', '0', 'important');
    clone.style.setProperty('opacity', '1', 'important');
    clone.style.setProperty('filter', 'none', 'important');
    
    // Force l'opacité sur tous les descendants
    clone.querySelectorAll('*').forEach(el => {
      (el as HTMLElement).style.setProperty('opacity', '1', 'important');
      (el as HTMLElement).style.setProperty('filter', 'none', 'important');
    });

    wrapper.appendChild(clone);
  });

  // Ajouter le watermark si spécifié
  if (options.watermark) {
    wrapper.appendChild(createWatermark(options.watermark.logoPath, options.watermark.text));
  }

  document.body.appendChild(wrapper);

  await waitForImages(wrapper);
  await new Promise(r => requestAnimationFrame(r));

  const canvas = await html2canvas(wrapper, {
    background: backgroundColor,
    useCORS: true,
    allowTaint: true,
  });

  wrapper.remove();

  triggerDownload(canvas.toDataURL('image/png'), `${fileName}.png`);
};
